import { useEffect, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

import { readWebReleasePolicy, reportClientStatus } from "./release-policy";
import { evaluateUpdateState, parseAppVersion, type UpdateState } from "./version";

const OK_STATE: UpdateState = { level: "ok", message: null, latestVersion: null };
const POLL_MS = 60_000;
const HEARTBEAT_MS = 60_000;

/**
 * Live remote update-control. On mount it reads the 'web' release policy and evaluates the running
 * __APP_VERSION__ against it, then keeps current via Realtime (instant when an operator flips
 * force_update / bumps latest_version) with a poll fallback. It also heartbeats this client's
 * version through upsert_client_app_status so operators can confirm a release is live in the field.
 *
 * Composes the unit-tested pure core (version.ts) + I/O wrappers (release-policy.ts); the
 * supabase client is injected so it is testable with a fake.
 */
export function useUpdateControl(
  client: SupabaseClient,
  identity: { accountId: string; actorId: string },
): UpdateState {
  const [state, setState] = useState<UpdateState>(OK_STATE);

  useEffect(() => {
    let active = true;
    const { version, sha } = parseAppVersion(__APP_VERSION__);

    async function refresh(): Promise<void> {
      const policy = await readWebReleasePolicy(client);
      if (!active || policy === null) {
        return;
      }
      setState(evaluateUpdateState({ currentVersion: version, policy }));
    }

    async function heartbeat(): Promise<void> {
      await reportClientStatus(client, {
        accountId: identity.accountId,
        actorId: identity.actorId,
        appVersion: version,
        buildSha: sha,
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
    }

    void refresh();
    void heartbeat();

    const pollId = setInterval(() => void refresh(), POLL_MS);
    const beatId = setInterval(() => void heartbeat(), HEARTBEAT_MS);

    const channel = client
      .channel("app_release_policy:web")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "app_release_policy", filter: "channel=eq.web" },
        () => void refresh(),
      )
      .subscribe();

    return () => {
      active = false;
      clearInterval(pollId);
      clearInterval(beatId);
      void client.removeChannel(channel);
    };
  }, [client, identity.accountId, identity.actorId]);

  return state;
}
